/**
 * Support-impersonation consent screen — Owner-only (Requirement 24).
 *
 * An Owner grants or revokes a stored, revocable, time-boxed consent that lets
 * a Support user act within the tenant. The consent is a *backend* concept
 * (a 1–60 minute tenant consent granted by an Owner); this screen is the thin
 * UI over it. Per the module boundary, `web` imports no backend runtime — the
 * actual grant/revoke calls are INJECTED as props (`onGrantConsent` /
 * `onRevokeConsent`), and the current consent state is injected too. The screen
 * only renders controls, client-validates the time-box range, and presents the
 * Platform's success/error responses through the accessible {@link UserMessage}.
 *
 * Requirement mapping:
 *   - 24.1 Owner sees grant + revoke controls.
 *   - 24.2 Grant submits a time-box between 1 and 60 minutes; the range is
 *          client-validated before the injected call is made.
 *   - 24.3 On a confirmed grant, a success User_Message states consent is active
 *          and the exact expiry date/time (from the Platform response).
 *   - 24.4 On a denied/failed grant, an error User_Message (what/why/next); the
 *          prior state is left inactive.
 *   - 24.5 On a confirmed revoke, a success User_Message states consent is revoked.
 *   - 24.6 On a denied/failed revoke, an error User_Message; prior state unchanged.
 *   - 24.8 A non-Owner sees only a restricted User_Message and no controls
 *          (authorization is enforced by the Platform regardless — the hidden
 *          controls are never the sole control).
 *   - 22.11 Every user-facing string resolves through the i18n catalog (`useT`).
 *   - 26.x  All feedback is rendered through {@link UserMessage}.
 *
 * SSR note: the Owner controls (time-box input, grant + revoke buttons) are in
 * the initial render output before any interaction, so the server-rendered HTML
 * contains real content (Req 21.1). Async results update local state on the
 * client after hydration.
 */
import { useCallback, useState, type ReactElement } from "react";
import type { ErrorEnvelope } from "@avos/foundation";
import { useT } from "../i18n/index.js";
import {
  UserMessage,
  toUserMessage,
  type UserMessageModel,
} from "../components/UserMessage.js";

/** The four tenant RBAC roles (Req 12.1). Only `Owner` may use this screen. */
export type TenantRole = "Owner" | "Manager" | "Staff" | "Accountant";

/** The inclusive lower bound of the consent time-box, in minutes (Req 24.2). */
export const TIME_BOX_MIN_MINUTES = 1;
/** The inclusive upper bound of the consent time-box, in minutes (Req 24.2). */
export const TIME_BOX_MAX_MINUTES = 60;
/** The default time-box shown in the input (a middle-of-range convenience). */
export const TIME_BOX_DEFAULT_MINUTES = 30;

/**
 * The current, injected consent state (Req 24 surfaces the backend record).
 * `active` reflects whether an unexpired consent exists; `expiresAt` is the
 * human-readable expiry the backend supplies (already formatted for display, so
 * the SSR-safe pure component never reads a clock).
 */
export interface ImpersonationConsentState {
  /** Whether an unexpired support-impersonation consent is currently in effect. */
  readonly active: boolean;
  /** Human-readable expiry timestamp when `active`, else undefined. */
  readonly expiresAt?: string;
}

/**
 * The result of an injected grant call. On success the backend returns the
 * human-readable expiry to echo back to the Owner (Req 24.3); on failure it
 * returns the stable {@link ErrorEnvelope} (Req 24.4).
 */
export type GrantConsentResult =
  | { readonly ok: true; readonly expiresAt: string }
  | { readonly ok: false; readonly error: ErrorEnvelope };

/**
 * The result of an injected revoke call: success (Req 24.5) or an
 * {@link ErrorEnvelope} (Req 24.6).
 */
export type RevokeConsentResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: ErrorEnvelope };

/** Props for {@link ImpersonationConsentScreen}. */
export interface ImpersonationConsentScreenProps {
  /**
   * The signed-in user's tenant role. Only `Owner` sees the controls (Req 24.8).
   * Defaults to `"Owner"` so the screen registry (`screens.tsx`), which renders
   * screens with no props, shows the Owner controls in the SSR output; a host
   * supplies the real signed-in role.
   */
  readonly role?: TenantRole;
  /** The injected current consent state (Req 24 surfaces the backend record). */
  readonly consent?: ImpersonationConsentState;
  /**
   * Injected grant call: records a time-boxed consent for `timeBoxMinutes`
   * minutes (1–60). Web imports no backend runtime, so the actual grant is
   * provided by the host. (Req 24.2) Defaults to a no-op that reports the
   * feature is not wired, so the placeholder registry render is inert.
   */
  readonly onGrantConsent?: (timeBoxMinutes: number) => Promise<GrantConsentResult>;
  /** Injected revoke call: withdraws the current consent. (Req 24.5) */
  readonly onRevokeConsent?: () => Promise<RevokeConsentResult>;
}

/**
 * The default injected error envelope used when no host handler is wired (the
 * placeholder registry render). Uses a generic diagnostic code; a real host
 * always supplies `onGrantConsent`/`onRevokeConsent`.
 */
function unwiredEnvelope(): ErrorEnvelope {
  return {
    error: {
      code: "AUTHZ_DENIED",
      message: "Support access is not available here.",
      detail: "This screen is not connected to the Platform in this context.",
      next: "Open support access from your account settings.",
      request_id: "",
    },
  };
}

/** Default grant handler: reports the feature is not wired (placeholder). */
async function defaultOnGrantConsent(): Promise<GrantConsentResult> {
  return { ok: false, error: unwiredEnvelope() };
}

/** Default revoke handler: reports the feature is not wired (placeholder). */
async function defaultOnRevokeConsent(): Promise<RevokeConsentResult> {
  return { ok: false, error: unwiredEnvelope() };
}

/** What kind of feedback the screen is currently showing. */
type Feedback =
  | { readonly kind: "none" }
  | { readonly kind: "error"; readonly model: UserMessageModel }
  | { readonly kind: "status"; readonly model: UserMessageModel };

/**
 * Validate the time-box against the inclusive 1–60 minute range (Req 24.2).
 * Pure and exported so the property/unit tests can exercise the range directly.
 * Accepts only whole minutes: a non-integer, NaN, or out-of-range value fails.
 */
export function isValidTimeBox(minutes: number): boolean {
  return (
    Number.isInteger(minutes) &&
    minutes >= TIME_BOX_MIN_MINUTES &&
    minutes <= TIME_BOX_MAX_MINUTES
  );
}

export default function ImpersonationConsentScreen({
  role = "Owner",
  consent,
  onGrantConsent = defaultOnGrantConsent,
  onRevokeConsent = defaultOnRevokeConsent,
}: ImpersonationConsentScreenProps = {}): ReactElement {
  const t = useT();

  // The time-box input value, as a string so partial/invalid input is
  // representable (an empty or non-numeric field is caught by validation).
  const [timeBox, setTimeBox] = useState<string>(
    String(TIME_BOX_DEFAULT_MINUTES),
  );
  const [feedback, setFeedback] = useState<Feedback>({ kind: "none" });
  // Whether an injected call is in flight (disables controls, prevents double
  // submit). Never true during SSR.
  const [busy, setBusy] = useState<boolean>(false);

  const handleGrant = useCallback(async () => {
    const minutes = Number(timeBox);
    // Client-side range validation before any injected call (Req 24.2).
    if (!isValidTimeBox(minutes)) {
      setFeedback({
        kind: "error",
        model: {
          what: t("admin.impersonation.invalidRange.what"),
          why: t("admin.impersonation.invalidRange.why"),
          next: t("admin.impersonation.invalidRange.next"),
        },
      });
      return;
    }
    setBusy(true);
    try {
      const result = await onGrantConsent(minutes);
      if (result.ok) {
        // Req 24.3: confirm active + state the exact expiry.
        setFeedback({
          kind: "status",
          model: {
            what: t("admin.impersonation.grantSuccess.what"),
            why: t("admin.impersonation.grantSuccess.why"),
            next: t("admin.impersonation.grantSuccess.next", {
              expiresAt: result.expiresAt,
            }),
          },
        });
      } else {
        // Req 24.4: consent not granted; present the Platform's reason.
        setFeedback({ kind: "error", model: toUserMessage(result.error) });
      }
    } finally {
      setBusy(false);
    }
  }, [timeBox, onGrantConsent, t]);

  const handleRevoke = useCallback(async () => {
    setBusy(true);
    try {
      const result = await onRevokeConsent();
      if (result.ok) {
        // Req 24.5: confirm revoked.
        setFeedback({
          kind: "status",
          model: {
            what: t("admin.impersonation.revokeSuccess.what"),
            why: t("admin.impersonation.revokeSuccess.why"),
            next: t("admin.impersonation.revokeSuccess.next"),
          },
        });
      } else {
        // Req 24.6: not revoked; prior state unchanged, present the reason.
        setFeedback({ kind: "error", model: toUserMessage(result.error) });
      }
    } finally {
      setBusy(false);
    }
  }, [onRevokeConsent, t]);

  // Non-Owner: restricted message only, no controls (Req 24.8). The Platform
  // still enforces authorization; the hidden controls are never the sole gate.
  if (role !== "Owner") {
    return (
      <section
        aria-labelledby="screen-heading"
        data-screen="impersonationConsent"
        data-restricted="true"
      >
        <h1 id="screen-heading">{t("admin.impersonation.title")}</h1>
        <UserMessage
          severity="status"
          message={{
            what: t("admin.impersonation.restricted.what"),
            why: t("admin.impersonation.restricted.why"),
            next: t("admin.impersonation.restricted.next"),
          }}
        />
      </section>
    );
  }

  const active = consent?.active ?? false;

  return (
    <section
      aria-labelledby="screen-heading"
      data-screen="impersonationConsent"
    >
      <h1 id="screen-heading">{t("admin.impersonation.title")}</h1>
      <p>{t("admin.impersonation.prompt")}</p>

      <p data-testid="consent-status" data-active={String(active)}>
        {active
          ? t("admin.impersonation.statusActive")
          : t("admin.impersonation.statusInactive")}
      </p>

      {/* Grant controls (Req 24.1, 24.2): a time-box input constrained to
          1–60 minutes plus the grant action. `min`/`max`/`step` give native
          UA hints; `isValidTimeBox` is the authoritative check. */}
      <div className="impersonation-consent__grant">
        <label htmlFor="impersonation-timebox">
          {t("admin.impersonation.timeBoxLabel")}
        </label>
        <input
          id="impersonation-timebox"
          name="timeBoxMinutes"
          type="number"
          inputMode="numeric"
          min={TIME_BOX_MIN_MINUTES}
          max={TIME_BOX_MAX_MINUTES}
          step={1}
          value={timeBox}
          disabled={busy}
          aria-describedby="impersonation-timebox-hint"
          onChange={(event) => setTimeBox(event.target.value)}
        />
        <span id="impersonation-timebox-hint">
          {t("admin.impersonation.timeBoxHint")}
        </span>
        <button
          type="button"
          data-testid="grant-consent"
          disabled={busy}
          onClick={() => void handleGrant()}
        >
          {t("admin.impersonation.grant")}
        </button>
      </div>

      {/* Revoke control (Req 24.1). Always present for the Owner; the backend
          rejects a revoke with no active consent, surfaced via Req 24.6. */}
      <div className="impersonation-consent__revoke">
        <button
          type="button"
          data-testid="revoke-consent"
          disabled={busy}
          onClick={() => void handleRevoke()}
        >
          {t("admin.impersonation.revoke")}
        </button>
      </div>

      {feedback.kind !== "none" ? (
        <UserMessage
          severity={feedback.kind === "error" ? "error" : "status"}
          message={feedback.model}
        />
      ) : null}
    </section>
  );
}
